// ─── Post-Processing Atmosphere Pass ────────────────────────────────────────
//
// Reusable, perf-safe rendering helpers shared by the overworld renderer and
// the battle scene. The goal is an HD-2D feel (the *post-processing* of Octopath
// Traveler / Sea of Stars) layered OVER the existing pixel art — light, depth,
// grade, atmosphere — WITHOUT any new sprite work and WITHOUT ever blurring the
// crisp base scene.
//
// PERFORMANCE CONTRACT (this runs every frame, target 60fps):
//   • Bloom runs at QUARTER resolution on a cached offscreen canvas.
//   • Gradients (haze / vignette) are precomputed and only rebuilt on resize.
//   • Nothing is allocated in the hot path — buffers persist across frames.
//   • The base scene keeps imageSmoothingEnabled=false; only the GLOW/haze
//     overlays are soft. We never read-modify-write the base scene pixels.
//
// Everything is gated behind POSTFX_ENABLED and per-effect intensity constants
// so the whole pass can be tuned or disabled from one place.

import { AmbientParticles } from "./ambient-particles";
export { AmbientParticles } from "./ambient-particles";
export type { ParticleKind, ParticleBounds } from "./ambient-particles";

/** Master switch. Set false to disable the entire atmosphere pass. */
export const POSTFX_ENABLED = true;

/** Per-effect intensity knobs — tune these, not the math below. */
export const POSTFX_INTENSITY = {
  grade: 1.2, // color-grade strength multiplier
  bloom: 0.65, // additive bloom alpha
  haze: 0.9, // depth-haze strength multiplier
  vignette: 1.25, // vignette strength multiplier
} as const;

// ── Color grade keys ────────────────────────────────────────────────────────
// Per-mood tints, all sampled from the PIXEL_STYLE master palette so the grade
// reads as the same world rather than a Photoshop filter. Each grade is a pair:
//   multiply — darkens/tints shadows toward the mood (composite 'multiply')
//   screen   — lifts highlights toward a warm/cool light (composite 'screen')
// Strengths are deliberately subtle (err subtle per the art direction).
export type GradeKey =
  | "grass"
  | "cave"
  | "office"
  | "creative"
  | "ops"
  | "day"
  | "night"
  | "dawn"
  | "dusk";

interface Grade {
  // multiply layer: rgb + alpha
  mul: [number, number, number, number];
  // screen layer: rgb + alpha
  scr: [number, number, number, number];
}

// Palette refs: cream f5ecd6, sand b89968, walnut 5c4a30, moss 7a9a4a,
// forest 5c7935, ocean 6a9bc4, deep 3d6890, gold c5a83a, mystic a04ab8,
// alert c54a4a, ink 1a1a1a.
const GRADES: Record<GradeKey, Grade> = {
  // Sun-warmed foliage: faint gold lift, gentle moss multiply in shadow.
  grass: {
    mul: [180, 196, 150, 0.1],
    scr: [197, 168, 58, 0.07],
  },
  // Dev dungeon: cool deep-ocean multiply, faint pale-sky monitor glow lift.
  cave: {
    mul: [61, 104, 144, 0.22],
    scr: [108, 155, 196, 0.06],
  },
  // Office floors: neutral-warm earth — slight walnut multiply, cream lift.
  office: {
    mul: [138, 122, 92, 0.1],
    scr: [245, 236, 214, 0.05],
  },
  // Creative lab: mystic-purple cast with a soft cream sparkle lift.
  creative: {
    mul: [160, 120, 184, 0.12],
    scr: [188, 212, 160, 0.06],
  },
  // Ops center: warm amber server-heat — gold lift, walnut multiply.
  ops: {
    mul: [120, 100, 80, 0.12],
    scr: [197, 138, 70, 0.08],
  },
  // Time-of-day grades — composed ON TOP of the zone grade by the caller, so
  // the existing day-night tint reads as light rather than a flat overlay.
  day: {
    mul: [255, 255, 255, 0],
    scr: [255, 248, 224, 0.03],
  },
  // Warm gold sunrise lift.
  dawn: {
    mul: [197, 168, 58, 0.05],
    scr: [197, 168, 58, 0.1],
  },
  // Warm amber sunset — gold→alert-red lift, faint walnut multiply.
  dusk: {
    mul: [138, 100, 70, 0.07],
    scr: [197, 138, 70, 0.11],
  },
  // Cool deep-ocean night.
  night: {
    mul: [61, 104, 144, 0.2],
    scr: [108, 155, 196, 0.04],
  },
};

/**
 * Apply a per-mood color grade over the whole canvas (screen space).
 * Two cheap full-canvas fills (multiply + screen) — no pixel reads, no
 * allocation. `strength` scales both layers (0 disables).
 */
export function applyColorGrade(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  gradeKey: GradeKey,
  strength = 1,
): void {
  if (!POSTFX_ENABLED || strength <= 0) return;
  const g = GRADES[gradeKey] ?? GRADES.grass;
  const s = strength * POSTFX_INTENSITY.grade;
  const prev = ctx.globalCompositeOperation;
  const prevAlpha = ctx.globalAlpha;

  const mulA = g.mul[3] * s;
  if (mulA > 0.002) {
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = `rgba(${g.mul[0]}, ${g.mul[1]}, ${g.mul[2]}, ${mulA.toFixed(3)})`;
    ctx.fillRect(0, 0, w, h);
  }
  const scrA = g.scr[3] * s;
  if (scrA > 0.002) {
    ctx.globalCompositeOperation = "screen";
    ctx.fillStyle = `rgba(${g.scr[0]}, ${g.scr[1]}, ${g.scr[2]}, ${scrA.toFixed(3)})`;
    ctx.fillRect(0, 0, w, h);
  }

  ctx.globalCompositeOperation = prev;
  ctx.globalAlpha = prevAlpha;
}

// ── Bloom ─────────────────────────────────────────────────────────────────
// Quarter-resolution bright-pass blur, composited back over the scene with
// 'lighter'. The offscreen buffers are cached and reused; they're only rebuilt
// when the canvas size changes. The blur is done by drawing the downsampled
// bright-pass through itself a couple of times with imageSmoothing ON (bilinear
// upscale = cheap gaussian-ish blur) — this NEVER touches the base scene.

interface BloomBuffers {
  w: number;
  h: number;
  qw: number;
  qh: number;
  bright: HTMLCanvasElement; // quarter-res bright pass
  blur: HTMLCanvasElement; // quarter-res blurred
  bctx: CanvasRenderingContext2D;
  blctx: CanvasRenderingContext2D;
}

let bloomBuffers: BloomBuffers | null = null;

function ensureBloomBuffers(w: number, h: number): BloomBuffers | null {
  if (bloomBuffers && bloomBuffers.w === w && bloomBuffers.h === h) {
    return bloomBuffers;
  }
  if (typeof document === "undefined") return null; // SSR guard
  const qw = Math.max(1, Math.floor(w / 4));
  const qh = Math.max(1, Math.floor(h / 4));
  const bright = document.createElement("canvas");
  bright.width = qw;
  bright.height = qh;
  const blur = document.createElement("canvas");
  blur.width = qw;
  blur.height = qh;
  const bctx = bright.getContext("2d");
  const blctx = blur.getContext("2d");
  if (!bctx || !blctx) return null;
  bloomBuffers = { w, h, qw, qh, bright, blur, bctx, blctx };
  return bloomBuffers;
}

export interface BloomOpts {
  alpha?: number; // composite alpha (defaults to POSTFX_INTENSITY.bloom)
  threshold?: number; // bright-pass cutoff 0..1 (default 0.6)
}

/**
 * Soft additive bloom. Reads the current canvas once into a quarter-res buffer,
 * isolates highlights via a 'multiply' threshold pass, blurs by bilinear
 * round-trip, then composites back with 'lighter'. All work is on cached
 * offscreen canvases — the base scene is never blurred.
 */
export function applyBloom(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  opts?: BloomOpts,
): void {
  if (!POSTFX_ENABLED) return;
  const alpha = (opts?.alpha ?? POSTFX_INTENSITY.bloom);
  if (alpha <= 0.01) return;
  const buf = ensureBloomBuffers(w, h);
  if (!buf) return;
  const threshold = opts?.threshold ?? 0.6;

  const { qw, qh, bctx, blctx, bright, blur } = buf;

  // 1) Downsample the live scene into the quarter-res bright buffer.
  bctx.globalCompositeOperation = "source-over";
  bctx.globalAlpha = 1;
  bctx.imageSmoothingEnabled = true;
  bctx.clearRect(0, 0, qw, qh);
  bctx.drawImage(ctx.canvas, 0, 0, w, h, 0, 0, qw, qh);

  // 2) Bright-pass: knock out the darks. A black overlay at 'multiply' scaled
  //    by the threshold crushes mids/shadows toward 0, leaving highlights.
  //    Then a 'screen' of itself would over-bright; instead we lift with a
  //    second copy. Cheap and good enough at quarter-res.
  bctx.globalCompositeOperation = "multiply";
  const cut = Math.round(threshold * 255);
  bctx.fillStyle = `rgb(${cut}, ${cut}, ${cut})`;
  bctx.fillRect(0, 0, qw, qh);
  // Re-expand remaining highlights so they bloom visibly.
  bctx.globalCompositeOperation = "screen";
  bctx.drawImage(bright, 0, 0);
  bctx.globalCompositeOperation = "source-over";

  // 3) Blur: bilinear down→up round-trip through the blur buffer (2 passes).
  blctx.globalCompositeOperation = "source-over";
  blctx.globalAlpha = 1;
  blctx.imageSmoothingEnabled = true;
  blctx.clearRect(0, 0, qw, qh);
  // Pass A: shrink to half, expand back (soft).
  const hw = Math.max(1, qw >> 1);
  const hh = Math.max(1, qh >> 1);
  blctx.drawImage(bright, 0, 0, qw, qh, 0, 0, hw, hh);
  blctx.drawImage(blur, 0, 0, hw, hh, 0, 0, qw, qh);
  // Pass B: a second softening round-trip for a wider glow.
  bctx.clearRect(0, 0, qw, qh);
  bctx.drawImage(blur, 0, 0, qw, qh, 0, 0, hw, hh);
  bctx.drawImage(bright, 0, 0, hw, hh, 0, 0, qw, qh);

  // 4) Composite the blurred highlights back over the scene, additively.
  const prev = ctx.globalCompositeOperation;
  const prevAlpha = ctx.globalAlpha;
  const prevSmooth = ctx.imageSmoothingEnabled;
  ctx.globalCompositeOperation = "lighter";
  ctx.globalAlpha = alpha;
  ctx.imageSmoothingEnabled = true; // upscale the soft glow only
  ctx.drawImage(bright, 0, 0, qw, qh, 0, 0, w, h);
  ctx.globalCompositeOperation = prev;
  ctx.globalAlpha = prevAlpha;
  ctx.imageSmoothingEnabled = prevSmooth; // restore crisp scene smoothing
}

// ── Depth haze ──────────────────────────────────────────────────────────────
// A soft vertical gradient that fades the top of the frame toward a horizon
// color — fakes atmospheric depth. Gradient is precomputed per (size,color)
// and only rebuilt when those change.

interface HazeCache {
  w: number;
  h: number;
  key: string;
  gradient: CanvasGradient;
}
let hazeCache: HazeCache | null = null;

// Default horizon tint: pale sky #cfe7f5 (water/sky ramp) — subtle.
const DEFAULT_HAZE = "rgba(207, 231, 245, 0.10)";

/**
 * Vertical horizon haze. The top ~45% of the frame receives a soft palette
 * tint that fades to nothing by mid-screen. Precomputed gradient; one fill.
 */
export function applyDepthHaze(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  color: string = DEFAULT_HAZE,
  strength = 1,
): void {
  if (!POSTFX_ENABLED) return;
  const s = strength * POSTFX_INTENSITY.haze;
  if (s <= 0.01) return;
  const key = color;
  if (!hazeCache || hazeCache.w !== w || hazeCache.h !== h || hazeCache.key !== key) {
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, color);
    grad.addColorStop(0.45, color.replace(/[\d.]+\)$/, "0)")); // same hue, alpha 0
    grad.addColorStop(1, color.replace(/[\d.]+\)$/, "0)"));
    hazeCache = { w, h, key, gradient: grad };
  }
  const prev = ctx.globalCompositeOperation;
  const prevAlpha = ctx.globalAlpha;
  ctx.globalCompositeOperation = "screen";
  ctx.globalAlpha = s;
  ctx.fillStyle = hazeCache.gradient;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = prev;
  ctx.globalAlpha = prevAlpha;
}

// ── Vignette ────────────────────────────────────────────────────────────────
// Radial corner darkening. Precomputed gradient, rebuilt only on resize. This
// is a slightly stronger, palette-correct vignette that can replace the
// renderer's existing one for a more cinematic frame.

interface VignetteCache {
  w: number;
  h: number;
  strength: number;
  gradient: CanvasGradient;
}
let vignetteCache: VignetteCache | null = null;

/**
 * Cinematic vignette. `strength` (default 1) scales the corner darkness.
 * Uses ink #1a1a1a so corners deepen toward the palette's "black".
 */
export function applyVignette(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  strength = 1,
): void {
  if (!POSTFX_ENABLED) return;
  const s = strength * POSTFX_INTENSITY.vignette;
  if (s <= 0.01) return;
  if (
    !vignetteCache ||
    vignetteCache.w !== w ||
    vignetteCache.h !== h ||
    vignetteCache.strength !== s
  ) {
    const r = Math.hypot(w, h) / 2;
    const grad = ctx.createRadialGradient(w / 2, h / 2, r * 0.5, w / 2, h / 2, r);
    grad.addColorStop(0, "rgba(26, 26, 26, 0)");
    grad.addColorStop(0.75, `rgba(26, 26, 26, ${(0.06 * s).toFixed(3)})`);
    grad.addColorStop(1, `rgba(26, 26, 26, ${(0.22 * s).toFixed(3)})`);
    vignetteCache = { w, h, strength: s, gradient: grad };
  }
  ctx.fillStyle = vignetteCache.gradient;
  ctx.fillRect(0, 0, w, h);
}

// Keep the named import referenced so tree-shakers don't drop the re-export
// when only the type is consumed elsewhere.
void AmbientParticles;
